import React, { useMemo } from "react";

import useTheme from "@mui/material/styles/useTheme";

import CircularProgress from "components/circular-progress";
import Dialog from "components/dialog";
import useStaffTasksV2 from "features/staff-tasks-v2/hooks/use-staff-tasks-v2.hook";
import { useGetStaffTaskQuery } from "services/staff-tasks";

import EditStaffTaskForm from "./edit-staff-task-form";
import TaskNotFound from "./task-not-found.component";
import ViewStaffTaskForm from "./view-staff-task-form";

export default function StaffTaskModalDialog() {
  const theme = useTheme();
  const {
    taskModalMode,
    isUpdating,
    currentTaskId,
    handleCloseTaskModal,
    handleOpenTaskModal,
    clearTaskListData
  } = useStaffTasksV2();

  const isNew = taskModalMode.displayMode === "create";
  const isEdit = taskModalMode.displayMode === "edit";
  const isView = taskModalMode.displayMode === "view";

  const {
    data: task,
    isFetching,
    isLoading,
    isUninitialized,
    error
  } = useGetStaffTaskQuery(Number(currentTaskId), {
    skip: !currentTaskId || isNew
  });

  const isBusy = useMemo(
    () => [isFetching, isLoading, isUninitialized, isUpdating].some(Boolean),
    [isFetching, isLoading, isUninitialized, isUpdating]
  );

  const taskDetails = useMemo(() => {
    if (error && "status" in error && error.status === 404) {
      return <TaskNotFound />;
    }

    if (isEdit || isNew) {
      return (
        <EditStaffTaskForm
          task={isEdit && !isBusy ? task : undefined}
          onSaveCallback={clearTaskListData}
        />
      );
    }

    if (currentTaskId === task?.id && isView) {
      return <ViewStaffTaskForm task={task} isBusy={isBusy} />;
    }

    return <CircularProgress sx={{ width: "100%" }} />;
  }, [
    clearTaskListData,
    currentTaskId,
    error,
    isBusy,
    isEdit,
    isNew,
    isView,
    task
  ]);

  return (
    <Dialog
      title={isEdit ? "Edit Task" : isNew ? "New Task" : "View Task"}
      onClose={(event: any, reason: string) => {
        if (
          (isEdit || isNew) &&
          (reason === "escapeKeyDown" || reason === "backdropClick")
        ) {
          return;
        }
        if (
          currentTaskId &&
          taskModalMode.onEditCancelMode === "openViewModal"
        ) {
          handleOpenTaskModal({ mode: "view", taskId: currentTaskId });
        } else {
          handleCloseTaskModal();
        }
      }}
      open={Boolean(isNew || isEdit || isView)}
      minWidth={1200}
      maxWidth="lg"
      sx={{
        zIndex: theme.zIndex.snackbar + 2
      }}
      dialogTitleSx={{ pl: "30px" }}
      dialogContentProps={{
        sx: {
          boxSizing: "border-box",
          padding: "40px 40px !important",
          minWidth: "1200px",
          maxWidth: "1200px",
          minHeight: "665px",
          maxHeight: "900px"
        }
      }}
    >
      {taskDetails}
    </Dialog>
  );
}
